

import { Warning as AlertTriangle } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { toSafeDate } from '@/lib/subscription-utils';
import { UserSubscription } from '@/types/subscription';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: UserSubscription;
}

export function CancelSubscriptionDialog({
  open,
  onClose,
  subscription,
}: CancelSubscriptionDialogProps) {
  const { backend } = useFirebase();
  const { toast } = useToast();
  const [cancelling, setCancelling] = useState(false);

  const handleConfirmCancel = async () => {
    if (!backend) return;

    setCancelling(true);

    try {
      await backend.billing.cancelSubscription();

      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription will remain active until the end of your billing period.',
      });

      onClose();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel subscription. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const periodEnd = toSafeDate(subscription?.currentPeriodEnd);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle sx={{ fontSize: 24, color: 'orange' }} />
          <Typography variant="h6">Cancel Subscription</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to cancel your subscription?
        </Alert>

        <Typography variant="body2" paragraph>
          If you cancel, you will:
        </Typography>

        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" paragraph>
            Lose access to all premium features
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Keep your current access until{' '}
            <strong>
              {periodEnd ? periodEnd.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }) : 'N/A'}
            </strong>
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Be able to reactivate at any time
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Your data will be preserved, and you can upgrade again whenever you're ready.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={cancelling}>
          Keep Subscription
        </Button>
        <Button
          onClick={handleConfirmCancel}
          color="error"
          variant="contained"
          disabled={cancelling}
          startIcon={cancelling ? <CircularProgress size={16} /> : null}
        >
          {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
